import React, { useState, useEffect, Fragment } from "react";
import Breadcrumb from "../components/common/breadcrumb";
import { useForm } from "react-hook-form";
import axiosConfig from "../services/axiosConfig";
import setHeader from "../components/Functions/setHeader";
import SweetAlert from 'sweetalert2';
import LoadingComponent from "../components/common/LoadingComponent";

const ManageFeatureCards = () => {
    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
    const [data, setData] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [image, setImage] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [featureFetchLoading, setFeatureFetchLoading] = useState(false);

    // Maximum allowed feature cards
    const MAX_FEATURES = 4;

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        setFeatureFetchLoading(true);
        setData([]); // Reset data before fetching new data

        axiosConfig.get("feature-list", setHeader())
            .then((res) => {
                setData(res.data.data || res.data);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setFeatureFetchLoading(false);
            });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const input = e.target;

        if (!file) return;
        // Check if file is PNG
        if (file.type !== 'image/png') {
            SweetAlert.fire({
                title: "Validation Error",
                text: "Only PNG files are allowed.",
                icon: "warning",
            }).then(() => {
                input.value = ""; // Clear the file input
            });
            return;
        }
        const maxSize = 200 * 1024; // 200KB

        // Check file size
        if (file.size > maxSize) {
            SweetAlert.fire({
                title: "Validation Error",
                text: "File size must be 200KB or less.",
                icon: "warning",
            }).then(() => {
                input.value = ""; // Clear the file input
            });
            return;
        }

        // Check image dimensions
        const img = new Image();
        img.onload = () => {
            if (img.width > 1000 || img.height > 1000) {
                SweetAlert.fire({
                    title: "Validation Error",
                    text: "Image dimensions must be at most 300px wide and 200px tall.",
                    icon: "warning",
                }).then(() => {
                    input.value = ""; // Clear the file input
                });
                return;
            }

            // ✅ All validations passed
            setImage(file);
        };

        img.onerror = () => {
            SweetAlert.fire({
                title: "Invalid Image",
                text: "Could not load the image.",
                icon: "error",
            }).then(() => {
                input.value = ""; // Clear the file input
            });
        };

        img.src = URL.createObjectURL(file);
    };

    const onSubmit = data => {
        if (data !== '') {
            // Check limit for new features (not in edit mode)
            if (!editMode && data.length >= MAX_FEATURES) {
                SweetAlert.fire({
                    title: "Limit Reached",
                    text: `Maximum ${MAX_FEATURES} feature cards are allowed. Please delete an existing feature to add a new one.`,
                    icon: "warning",
                });
                return;
            }

            setIsLoading(true);

            // Create FormData for handling file upload
            const formData = new FormData();
            formData.append("title", data.title);
            formData.append("description", data.description || "");

            if (editMode) {
                formData.append("id", editId);

                // Handle image for edit mode
                if (image) {
                    // New image selected
                    formData.append("image", image);
                } else if (existingImageUrl) {
                    // Keep existing image
                    formData.append("existing_image_url", existingImageUrl);
                    formData.append("keep_existing_image", "1");
                } else {
                    // No image
                    formData.append("image", "");
                }

                axiosConfig.post(`update-feature`, formData, setHeader()).then((res) => {
                    setIsLoading(false);
                    if (res.data.error && res.data.messages === 'Feature title already exists!') {
                        SweetAlert.fire({
                            title: "Error",
                            text: "Feature title already exists!",
                            icon: "warning",
                        });
                        return; // STOP here
                    }
                    if (!res.data.error) {
                        SweetAlert.fire({ title: "Success", text: "Updated Successfully", icon: "success" });
                        resetForm();
                        getData();
                    } else {
                        SweetAlert.fire({ title: "Oops!", text: "Failed to update. Try again!", icon: "error" });
                    }
                }).catch((error) => {
                    setIsLoading(false);
                    console.log(error);
                });
            } else {
                // Add mode - image is optional, set empty string if no image
                if (image) {
                    formData.append("image", image);
                } else {
                    formData.append("image", "");
                }

                axiosConfig.post("insert-feature", formData, setHeader()).then((res) => {
                    setIsLoading(false);
                    if (res.data.error && res.data.messages === 'Feature title already exists!') {
                        SweetAlert.fire({
                            title: "Error",
                            text: "Feature title already exists!",
                            icon: "warning",
                        });
                        return; // STOP here
                    }
                    if (!res.data.error) {
                        SweetAlert.fire({ title: "Success", text: "Feature Added Successfully", icon: "success" });
                        resetForm();
                        getData();
                    } else {
                        SweetAlert.fire({ title: "Oops!", text: "Failed to add feature. Try again!", icon: "error" });
                    }
                }).catch((error) => {
                    setIsLoading(false);
                    console.log(error);
                });
            }
        } else {
            errors.showMessages();
        }
    };

    const resetForm = () => {
        reset();
        setImage(null);
        setExistingImageUrl("");
        setEditMode(false);
        setEditId(null);

        // Reset file input
        const fileInput = document.getElementById("image");
        if (fileInput) fileInput.value = "";
    };

    const editData = (id) => {
        const featureToEdit = data.find((item) => item.id === id);
        if (featureToEdit) {
            setEditMode(true);
            setEditId(id);

            // Populate form fields
            setValue("title", featureToEdit.title || featureToEdit.name);
            setValue("description", featureToEdit.description);

            // Set existing image URL for display
            setExistingImageUrl(featureToEdit.image || "");
            setImage(null); // Reset file input
        }
    };

    const DeleteData = (id) => {
        SweetAlert.fire({
            title: 'Are you sure?',
            text: "Once deleted, you will not be able to recover this feature!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ok',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axiosConfig.get(`delete-feature/${id}`, setHeader())
                    .then((res) => {
                        if (!res.data.error) {
                            SweetAlert.fire({
                                title: "Success",
                                text: res.data.messages || "Feature deleted successfully",
                                icon: "success",
                            });
                            getData(); // Refresh the list
                        } else {
                            SweetAlert.fire({
                                title: "Oops!",
                                text: res.data.messages || "Failed to delete feature",
                                icon: "error",
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Error deleting data:", error);
                        SweetAlert.fire({
                            title: "Error",
                            text: "An error occurred while deleting the feature.",
                            icon: "error",
                        });
                    });
            }
        });
    };

    // Check if we've reached the maximum limit
    const isMaxLimitReached = data.length >= MAX_FEATURES;

    if (featureFetchLoading || isLoading) {
        return (
            <LoadingComponent />
        );
    }

    return (
        <Fragment>
            <Breadcrumb title="Feature Cards" parent="dashboard" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-5">
                        <div className="card">
                            <div className="card-header">
                                <h5>
                                    {editMode ? 'Edit Feature Card' : 'Add New Feature Card'}
                                    <span className="text-muted" style={{ fontSize: '14px', marginLeft: '10px' }}>
                                        ({data.length}/{MAX_FEATURES})
                                    </span>
                                </h5>
                                {isMaxLimitReached && !editMode && (
                                    <div className="alert alert-warning mt-2 mb-0" style={{ padding: '8px 12px', fontSize: '13px' }}>
                                        <strong>Limit Reached!</strong> You can only have {MAX_FEATURES} feature cards. Delete an existing one to add new.
                                    </div>
                                )}
                            </div>
                            <div className="card-body">
                                <form className="needs-validation" noValidate="" onSubmit={handleSubmit(onSubmit)}>
                                    <div className="form-row">
                                        <div className="col-md-12 mb-3">
                                            <label htmlFor="validationCustom01">Title <span style={{ color: 'red', fontSize: '21px' }}>*</span></label>
                                            <input
                                                className="form-control"
                                                name="title"
                                                type="text"
                                                placeholder="Feature Title"
                                                {...register('title', { required: true })}
                                                disabled={isMaxLimitReached && !editMode}
                                            />
                                            <span>{errors.title && 'Title is required'}</span>
                                            <div className="valid-feedback">{"Looks good!"}</div>
                                        </div>

                                        <div className="col-md-12 mb-3">
                                            <label>Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                cols="5"
                                                name="description"
                                                placeholder="Feature Description..."
                                                {...register('description', { required: false })}
                                                disabled={isMaxLimitReached && !editMode}
                                            ></textarea>
                                        </div>

                                        <div className="col-md-12 mb-3">
                                            <label htmlFor="image">Feature Image {editMode && "(Leave empty to keep current image)"}</label>

                                            {/* Show existing image in edit mode */}
                                            {editMode && existingImageUrl && (
                                                <div className="existing-image-preview mb-2">
                                                    <img
                                                        src={existingImageUrl}
                                                        alt="Current feature"
                                                        style={{
                                                            maxWidth: "150px",
                                                            maxHeight: "100px",
                                                            objectFit: "cover",
                                                            border: "1px solid #ddd",
                                                            borderRadius: "4px"
                                                        }}
                                                    />
                                                    <p className="text-muted mt-1 mb-0" style={{ fontSize: "12px" }}>Current Image</p>
                                                </div>
                                            )}

                                            <input
                                                type="file"
                                                id="image"
                                                name="image"
                                                onChange={handleImageChange}
                                                className="form-control"
                                                accept="image/*"
                                                disabled={isMaxLimitReached && !editMode}
                                            />
                                          <small className="text-muted">Only PNG files ≤ 200KB and dimensions ≤ 1000×1000px are allowed.</small>

                                            {image && (
                                                <div className="file-preview mt-1">
                                                    <small className="text-info">New file selected: {image.name}</small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        disabled={isLoading || (isMaxLimitReached && !editMode)}
                                    >
                                        {isLoading
                                            ? `${editMode ? 'Updating' : 'Saving'}...`
                                            : (editMode ? 'Update' : 'Save')
                                        }
                                    </button>
                                    {(editMode || image) && (
                                        <button
                                            className="btn btn-secondary"
                                            style={{ marginLeft: '10px' }}
                                            type="button"
                                            onClick={resetForm}
                                            disabled={isLoading}
                                        >
                                            Reset
                                        </button>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-7">
                        <div className="card">
                            <div className="card-header">
                                <h5>Feature Cards List ({data.length}/{MAX_FEATURES})</h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th scope="col">{"#"}</th>
                                                <th scope="col">{"Title"}</th>
                                                <th scope="col">{"Description"}</th>
                                                <th scope="col">{"Image"}</th>
                                                <th scope="col">{"Action"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted">
                                                        No feature cards added yet
                                                    </td>
                                                </tr>
                                            ) : (
                                                data.map((item, index) => {
                                                    return (
                                                        <tr key={index}>
                                                            <th scope="row">{index + 1}</th>
                                                            <td>{item.title || item.name}</td>
                                                            <td>
                                                                {item.description ?
                                                                    (item.description.length > 50 ?
                                                                        `${item.description.substring(0, 50)}...` :
                                                                        item.description
                                                                    ) :
                                                                    "-"
                                                                }
                                                            </td>
                                                            <td>
                                                                {item.image ? (
                                                                  <div className="bg-primary rounded d-flex justify-content-center align-items-center">
                                                                      <img
                                                                        src={item.image}
                                                                        alt={item.title || item.name}
                                                                        style={{
                                                                            width: "30px",
                                                                            height: "30px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "4px"
                                                                        }}
                                                                    />
                                                                  </div>
                                                                ) : (
                                                                    <span className="text-muted">No Image</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    title="Edit"
                                                                    className="btn btn-xs btn-outline-success"
                                                                    onClick={(e) => { editData(item.id) }}
                                                                >
                                                                    Edit
                                                                </button>&nbsp;
                                                                <button
                                                                    title="Delete"
                                                                    className="btn btn-xs btn-outline-danger"
                                                                    onClick={(e) => DeleteData(item.id)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ManageFeatureCards;